import { Icons } from "@/assets/images";
import React from "react";
import { CustomModalProps } from "./type";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogClose,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";

const CustomModal = (props: CustomModalProps): React.ReactElement => {
  const {
    triggerChildren,
    title,
    description,
    descriptionNode,
    descriptionClassName,
    overlayClassName,
    showCloseButton = false,
    onCloseButtonClick,
    children,
    cancelText = "Cancel",
    actionText = "Continue",
    onAction,
    onCancel,
    open,
    onOpenChange,
    className,
    variant = "default",
    personName,
    actionVariant,
    disableEscapeClose = false,
  } = props;

  const finalDescription = description?.replace("{name}", personName ?? "");

  const iconMap = {
    destructive: {
      src: Icons.deleteModalIcon,
      alt: "Delete-confirmation-icon",
    },
    updated: {
      src: Icons.successIcon,
      alt: "Updated-icon",
    },
    accepted: {
      src: Icons.successIcon,
      alt: "Success-icon",
    },
    warning: {
      src: Icons.warningIcon,
      alt: "Warning-icon",
    },
    default: {
      src: Icons.infoIcon,
      alt: "Information-icon",
    },
    rejected: {
      src: Icons.rejectedIcon,
      alt: "Rejected-icon",
    },
    error: {
      src: Icons.errorIcon,
      alt: "Error-icon",
    },
    inactive: {
      src: Icons.inactiveIcon,
      alt: "Inactive-icon",
    },
    authShield: {
      src: Icons.authShieldIcon,
      alt: "Auth-shield-icon",
    },
  };

  const selectedIcon = iconMap[variant];
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = typeof open === "boolean";
  const isOpen = isControlled ? open : internalOpen;
  const handleOpenChange = (nextOpen: boolean): void => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      {triggerChildren && (
        <AlertDialogTrigger asChild>{triggerChildren}</AlertDialogTrigger>
      )}

      <AlertDialogContent
        className={className}
        overlayClassName={overlayClassName}
        onEscapeKeyDown={(event) => {
          if (disableEscapeClose) {
            event.preventDefault();
          }
        }}
      >
        {showCloseButton && (
          <AlertDialogClose
            onClick={
              onCloseButtonClick ?? ((): void => handleOpenChange(false))
            }
          />
        )}
        {selectedIcon?.src && (
          <img
            src={selectedIcon.src}
            alt={selectedIcon.alt}
            className="mx-auto size-14"
          />
        )}

        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {(descriptionNode || description) &&
            (descriptionNode ? (
              <AlertDialogDescription asChild className={descriptionClassName}>
                <div>{descriptionNode}</div>
              </AlertDialogDescription>
            ) : (
              <AlertDialogDescription className={descriptionClassName}>
                {finalDescription}
              </AlertDialogDescription>
            ))}
          {children}
        </AlertDialogHeader>

        <AlertDialogFooter>
          {cancelText !== "" && (
            <AlertDialogCancel onClick={onCancel}>
              {cancelText}
            </AlertDialogCancel>
          )}
          <AlertDialogAction onClick={onAction} variant={actionVariant}>
            {actionText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CustomModal;
